import { AlertCircle, Calendar, Church, Mail } from 'lucide-react'
import { useMemo, useState } from 'react'
import ConfirmSendBulkEmails from './ConfirmSendBulkEmails'
import type { annualSummaryReportsSchema } from '@/schemas/filterSchemas'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { usePermissions } from '@/utils/permissions'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatCurrency } from '@/utils/currencyUtils'
import { CURRENT_YEAR } from '@/features/receipts/constants/receipt'
import { useCleanQueryStates } from '@/hooks'

export default function AnnualReportsCard() {
  const [query, setQuery] = useCleanQueryStates<
    typeof annualSummaryReportsSchema
  >('/authed/annual-summary')

  const churches = useChurchesStore((s) => s.churches)
  const donations = useDonationsStore((s) => s.donations)
  const members = useMembersStore((s) => s.members)

  const years = useMemo(() => {
    if (!donations.length) return []
    const allYears = donations
      .map((d) => new Date(d.date).getFullYear())
      .filter((y) => !isNaN(y))
    if (!allYears.length) return []
    const minYear = Math.min(...allYears)
    const maxYear = CURRENT_YEAR - 1
    const result: Array<number> = []
    for (let y = maxYear; y >= minYear; y--) {
      result.push(y)
    }
    return result
  }, [donations])

  // Get permissions for role-based access control
  const permissions = usePermissions()

  const [showDetails, setShowDetails] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    churchId?: string
  }>({ open: false })

  // Filter churches based on user's role and assignments
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredChurches = useMemo(() => {
    return assignedChurchIds.length === 0
      ? churches // Super admin sees all churches
      : churches.filter((church) => assignedChurchIds.includes(church._id))
  }, [churches, assignedChurchIds])

  // Filter donations for the selected year
  const yearDonations = useMemo(() => {
    return donations.filter((donation) => {
      const donationYear = new Date(donation.date).getFullYear()
      return (
        donationYear === Number(query.year) &&
        donation.status === 'verified' &&
        !donation.isRegistrationFee
      )
    })
  }, [donations, query.year])

  // Group donations by church and calculate stats
  const churchStats = useMemo(() => {
    const stats = filteredChurches
      .map((church) => {
        const churchDonations = yearDonations.filter(
          (d) => d.churchId === church._id,
        )
        const donorIds = [...new Set(churchDonations.map((d) => d.donorId))]
        const donors = members.filter(
          (m) => donorIds.includes(m._id) && m.active,
        )
        const totalAmount = churchDonations.reduce(
          (sum, d) => sum + d.amount,
          0,
        )

        return {
          church,
          donorCount: donors.length,
          totalDonations: churchDonations.length,
          totalAmount,
          donors,
          donations: churchDonations,
        }
      })
      .filter((stat) => stat.donorCount > 0) // Only churches with donors

    return stats.sort((a, b) => b.donorCount - a.donorCount)
  }, [filteredChurches, yearDonations, members])

  const totalDonors = churchStats.reduce(
    (sum, stat) => sum + stat.donorCount,
    0,
  )

  const handleOpenConfirmDialog = (churchId?: string) => {
    setConfirmDialog({ open: true, churchId })
  }

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false })
  }

  if (years.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <h3 className="font-semibold text-gray-900 mb-2">
          Annual Donation Summaries
        </h3>
        <p className="text-gray-600">No annual reports are available.</p>
        <p className="text-sm text-gray-500">
          There are no donations from previous years to generate annual
          summaries.
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
              <Mail className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                Annual Donation Summaries
              </h3>
              <p className="text-sm text-gray-600">
                Send yearly reports to donors
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Select
              value={query.year ? String(query.year) : undefined}
              onValueChange={(value) => setQuery({ year: Number(value) })}
            >
              <SelectTrigger className="w-30">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4 flex-1 min-h-0">
          {/* Summary Stats */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {churchStats.length}
                </div>
                <div className="text-sm text-gray-600">Churches</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {totalDonors}
                </div>
                <div className="text-sm text-gray-600">Total Donors</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              onClick={() => handleOpenConfirmDialog()}
              className="w-full"
              disabled={totalDonors === 0}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send All Reports ({totalDonors} emails)
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowDetails(!showDetails)}
              className="w-full"
              disabled={churchStats.length === 0}
            >
              {showDetails ? 'Hide Details' : 'View by Church'}
            </Button>
          </div>

          {/* Church Details */}
          {showDetails && (
            <div className="space-y-3 border-t pt-4 flex-1 min-h-0 overflow-y-auto">
              {churchStats.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No donations found for {query.year}</p>
                  <p className="text-sm">Try selecting a different year</p>
                </div>
              ) : (
                churchStats.map((stat) => (
                  <div
                    key={stat.church._id}
                    className="border border-gray-200 rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Church className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-sm">
                            {stat.church.name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 ml-6">
                          {stat.church.address.city},{' '}
                          {stat.church.address.province}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {stat.donorCount} donors
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between ml-6">
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span>{stat.totalDonations} donations</span>
                        <span>{formatCurrency(stat.totalAmount)}</span>
                      </div>
                      <Button
                        onClick={() => handleOpenConfirmDialog(stat.church._id)}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Send ({stat.donorCount})
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmSendBulkEmails
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        year={query.year}
        churchId={confirmDialog.churchId}
        churchStats={churchStats}
      />
    </>
  )
}
